"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

const LOCATION_STORAGE_KEY = "LOCATION_ENABLED";

const GEO_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  timeout: 15000,
  maximumAge: 30000,
};

export type LocationStatus = "idle" | "loading" | "granted" | "denied";

export type CurrentLocation = {
  latitude: number;
  longitude: number;
  accuracy: number;
  timestamp: number;
} | null;

type GeolocationContextType = {
  currentLocation: CurrentLocation;
  locationStatus: LocationStatus;
  requestLocation: () => void;
};

const GeolocationContext = createContext<GeolocationContextType | null>(null);

const deniedAlertContent = {
  heading: "We're having trouble finding your location.",
  textContent: (
    <p>
      Try refreshing the page and clicking Current Location again. If your
      browser asks for your location, click <b>Allow</b> or{" "}
      <b>Grant Permissions</b>. If your browser doesn't prompt you, locate your
      browser's location services settings:
    </p>
  ),
  footerContent: (
    <div>
      <p style={{ marginBottom: 0 }}>
        <b>Safari</b>
      </p>
      <p>
        Click the <span style={{ color: "#397488" }}>Safari</span> tab, and
        then "
        <span style={{ color: "#397488" }}>Settings for urality.com</span>".
      </p>
      <p style={{ marginBottom: 0 }}>
        <b>Chrome</b>
      </p>
      <p>
        In your browser's{" "}
        <span style={{ color: "#397488" }}>address bar</span>, click the{" "}
        <span style={{ color: "#397488" }}>lock icon</span>, and ensure
        location is set to <span style={{ color: "#397488" }}>allow</span>.
      </p>
      <p style={{ marginBottom: 0 }}>
        <b>Firefox</b>
      </p>
      <p>
        Click{" "}
        <span style={{ color: "#397488" }}>Settings, Privacy & Security</span>
        , and find{" "}
        <span style={{ color: "#397488" }}>Location Permissions</span>.
      </p>
    </div>
  ),
};

export function GeolocationProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [currentLocation, setCurrentLocation] =
    useState<CurrentLocation>(null);
  const [locationStatus, setLocationStatus] = useState<LocationStatus>("idle");
  const watchIdRef = useRef<number | null>(null);

  const onSuccess = useCallback((position: GeolocationPosition) => {
    setCurrentLocation({
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      accuracy: position.coords.accuracy,
      timestamp: position.timestamp,
    });
    setLocationStatus("granted");
    try {
      localStorage.setItem(LOCATION_STORAGE_KEY, "true");
    } catch {
      // ignore
    }
  }, []);

  const onError = useCallback((_error: GeolocationPositionError) => {
    setLocationStatus("denied");
    setCurrentLocation(null);
    try {
      localStorage.setItem(LOCATION_STORAGE_KEY, "false");
    } catch {
      // ignore
    }
    if (
      typeof window !== "undefined" &&
      window.top === window.self &&
      (window as any).flash_messages
    ) {
      (window as any).flash_messages.alertModal(deniedAlertContent);
    }
  }, []);

  const startWatch = useCallback(() => {
    if (
      typeof window === "undefined" ||
      !window.navigator?.geolocation
    ) {
      setLocationStatus("denied");
      return;
    }
    // Avoid starting a second watcher if one is already running
    if (watchIdRef.current !== null) return;

    watchIdRef.current = window.navigator.geolocation.watchPosition(
      onSuccess,
      onError,
      GEO_OPTIONS
    );
  }, [onSuccess, onError]);

  // On mount: silently resume watching if the user already granted permission
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const stored = localStorage.getItem(LOCATION_STORAGE_KEY);
      if (stored === "true") {
        startWatch();
      }
    } catch {
      // ignore storage errors
    }

    return () => {
      if (watchIdRef.current !== null) {
        window.navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, [startWatch]);

  // Explicitly request location — triggers the browser permission prompt if needed
  const requestLocation = useCallback(() => {
    if (locationStatus === "loading" || locationStatus === "granted") return;
    setLocationStatus("loading");
    startWatch();
  }, [locationStatus, startWatch]);

  return (
    <GeolocationContext.Provider
      value={{ currentLocation, locationStatus, requestLocation }}
    >
      {children}
    </GeolocationContext.Provider>
  );
}

export function useCurrentLocation(): GeolocationContextType {
  const ctx = useContext(GeolocationContext);
  if (!ctx)
    throw new Error(
      "useCurrentLocation must be used within GeolocationProvider"
    );
  return ctx;
}
